// Layout.js
// Wraps every screen with:
//   • TopBar  — top-left menu button (Privacy / Terms / Log Out) + centered wordmark
//   • BottomNav — 5-tab bar mirroring the iOS app
//                 (Leaderboard, Dynasty, Home, Battles, Profile — Home centered)
//
// Usage: wrap App.js routes with <Layout> so every screen gets the shell.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import appstore from "../assets/appstore.png";
import houseIcon from "../assets/house-fill.svg";
import rankingIcon from "../assets/ranking-fill.svg";
import userIcon from "../assets/user-fill.svg";
import crownIcon from "../assets/crown-simple-fill.svg";
import shieldIcon from "../assets/shield-fill.svg";
import { useAuth } from "../managers/AuthContext";
import { listenToActivePickUpGames } from "../managers/matchmakingService";
import { listenToIncomingChallenges, isUserTurn } from "../managers/pickupService";
import { pacificDateString } from "../managers/pacificDate";

const APP_THEME = "#d4f53c";
const KNOCKOUT_LAVENDER = "#B899FF";
const TEAL = "#00BFB8";

// ─── Battles-tab dot helpers ────────────────────────────────────────────────────
// Mirrors the Battles tab dot in MainTabView.swift. Priority (when the tab is NOT active):
//   1. Lavender — Knockout Saturday reminder (once per Saturday, until Battles is tapped)
//   2. Yellow   — you have a pending turn in an active Pick Up game
//   3. Teal     — an incoming friend challenge is awaiting a response
// The Saturday reminder is dismissed by tapping Battles, persisted in localStorage by the
// Pacific calendar day (the web equivalent of iOS's UserDefaults date key).
const KNOCKOUT_DOT_KEY = "Rosterable_KnockoutDayDotSeenDate";

function isKnockoutSaturday() {
  return (
    new Date().toLocaleDateString("en-US", {
      timeZone: "America/Los_Angeles",
      weekday: "short",
    }) === "Sat"
  );
}
function shouldShowKnockoutDot() {
  if (!isKnockoutSaturday()) return false;
  try {
    return localStorage.getItem(KNOCKOUT_DOT_KEY) !== pacificDateString();
  } catch (_) {
    return false;
  }
}
function markKnockoutDotSeen() {
  try {
    localStorage.setItem(KNOCKOUT_DOT_KEY, pacificDateString());
  } catch (_) {
    /* localStorage unavailable — ignore */
  }
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
// Full 5-tab bar mirroring the iOS app, in the same order
// (Leaderboard · Dynasty · Home · Battles · Profile — Home centered).
// Dynasty and Battles point at their rules pages; nothing under those is
// playable on web yet (the rules pages carry an "iOS only" notice).

const TABS = [
  { id: "leaderboard", label: "LEADERBOARD", iconSrc: rankingIcon, path: "/leaderboard" },
  { id: "dynasty",     label: "DYNASTY",     iconSrc: crownIcon,   path: "/dynasty" },
  { id: "home",        label: "HOME",        iconSrc: houseIcon,   path: "/" },
  { id: "battles",     label: "BATTLES",     iconSrc: shieldIcon,  path: "/battles" },
  { id: "profile",     label: "PROFILE",     iconSrc: userIcon,    path: "/profile" },
];

// ─── Top Bar ──────────────────────────────────────────────────────────────────
// Centered wordmark with a top-left menu button. Privacy / Terms / Log Out now
// live inside the menu instead of inline links.

export function TopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout, freePlay, toggleFreePlay } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  function go(path) {
    setMenuOpen(false);
    navigate(path);
  }

  // Secret: 5 quick taps on the Home wordmark opens the hidden Roster Showcase.
  // On any other screen the wordmark just goes home as usual.
  const wordmarkTapsRef = useRef(0);
  const wordmarkTimerRef = useRef(null);
  function handleWordmarkTap() {
    if (pathname !== "/") { go("/"); return; }
    wordmarkTapsRef.current += 1;
    clearTimeout(wordmarkTimerRef.current);
    wordmarkTimerRef.current = setTimeout(() => { wordmarkTapsRef.current = 0; }, 600);
    if (wordmarkTapsRef.current >= 5) {
      wordmarkTapsRef.current = 0;
      navigate("/showcase");
    }
  }

  return (
    <>
      <div style={styles.topBar}>
        {/* Left: menu button */}
        <div style={styles.topBarSide}>
          <button
            style={styles.menuBtn}
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <span style={styles.menuIcon}>{menuOpen ? "✕" : "☰"}</span>
          </button>
        </div>

        {/* Center: wordmark (tap 5× on Home → hidden Roster Showcase) */}
        <button style={styles.wordmarkBtn} onClick={handleWordmarkTap}>
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        </button>

        {/* Right: spacer keeps the wordmark centered */}
        <div style={styles.topBarSide} />

        {/* Free Play toggle — Home only, logged-in only. Absolutely positioned
            in the right corner so it never shifts the centered wordmark. */}
        {user && pathname === "/" && (
          <button
            style={styles.freePlayToggle}
            onClick={toggleFreePlay}
            role="switch"
            aria-checked={freePlay}
            aria-label="Free Play"
            title={freePlay ? "Free Play is ON — solo drafts and streaks won't count" : "Free Play is OFF"}
          >
            <span style={{ ...styles.freePlayTrack, background: freePlay ? APP_THEME : "rgba(255,255,255,0.15)" }}>
              <span style={{ ...styles.freePlayKnob, transform: freePlay ? "translateX(14px)" : "translateX(0)" }} />
            </span>
            <span style={{ ...styles.freePlayLabel, color: freePlay ? APP_THEME : "rgba(255,255,255,0.45)" }}>
              FREE PLAY
            </span>
          </button>
        )}

        {/* Dropdown menu */}
        {menuOpen && (
          <>
            <div style={styles.menuBackdrop} onClick={() => setMenuOpen(false)} />
            <div style={styles.menuPanel} className="rb-menu-in">
              <button style={styles.menuItem} onClick={() => go("/privacy-policy")}>
                Privacy
              </button>
              <div style={styles.menuDivider} />
              <button style={styles.menuItem} onClick={() => go("/terms-of-service")}>
                Terms
              </button>
              <div style={styles.menuDivider} />
              <button style={styles.menuItem} onClick={() => go("/support")}>
                Support
              </button>
              {/* Always available: real users log out, everyone else (guests
                  and logged-out visitors) can reach the Sign In / Sign Up screen. */}
              <div style={styles.menuDivider} />
              <button
                style={{ ...styles.menuItem, color: APP_THEME }}
                onClick={() => {
                  setMenuOpen(false);
                  if (user) handleLogout();
                  else go("/login");
                }}
              >
                {user ? "Log Out" : "Sign In"}
              </button>
            </div>
          </>
        )}
      </div>
      <div style={styles.topBarRule} />

      <style>{`
        @keyframes rbMenuIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: none; }
        }
        .rb-menu-in { animation: rbMenuIn 0.16s ease; transform-origin: top left; }
      `}</style>
    </>
  );
}

// ─── Bottom Nav ───────────────────────────────────────────────────────────────
// Mirrors ModernTabBarItem in MainTabView.swift:
//   Selected: yellow pill behind icon, black icon, bold label
//   Unselected: no pill, dimmed icon + label

export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from current path
  function isActive(tab) {
    if (tab.path === "/" ) return location.pathname === "/";
    return location.pathname.startsWith(tab.path);
  }

  return (
    <>
      {/* Thin separator line */}
      <div style={styles.bottomRule} />

      <div style={styles.bottomNav}>
        {TABS.map(tab => {
          const active = isActive(tab);
          const Item = tab.id === "battles" ? BattlesTabItem : TabItem;
          return (
            <Item
              key={tab.id}
              tab={tab}
              isActive={active}
              onClick={() => navigate(tab.path)}
            />
          );
        })}
      </div>
    </>
  );
}

// ─── Tab Item ─────────────────────────────────────────────────────────────────

function TabItem({ tab, isActive, onClick, dotColor = null, accentColor = APP_THEME }) {
  const [isPressed, setIsPressed] = useState(false);

  return (
    <button
      style={{
        ...styles.tabItem,
        transform: isPressed ? "scale(0.93)" : "scale(1)",
        transition: "transform 0.1s ease",
      }}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {/* Icon with pill indicator when active */}
      <div style={styles.tabIconWrapper}>
        {isActive && (
          <div style={{ ...styles.tabPill, background: accentColor, boxShadow: `0 2px 8px ${accentColor}66` }} />
        )}
        <img
          src={tab.iconSrc}
          alt=""
          style={{
            ...styles.tabIcon,
            // Slightly smaller when active to match the iOS inset-within-pill look
            width:  isActive ? 22 : 24,
            height: isActive ? 22 : 24,
            // Active: black icon on the yellow pill. Inactive: dimmed white icon.
            // Source SVGs are solid black, so brightness(0) keeps them black and
            // invert(1) flips them to white before dimming with opacity.
            filter: isActive ? "brightness(0)" : "brightness(0) invert(1) opacity(0.45)",
            position: "relative",
            zIndex: 1,
          }}
        />
        {/* Notification dot (mirrors the iOS Battles tab). Hidden while the tab is active. */}
        {!isActive && dotColor && (
          <span style={{ ...styles.tabDot, background: dotColor, boxShadow: `0 0 4px ${dotColor}` }} />
        )}
      </div>

      {/* Label */}
      <span style={{
        ...styles.tabLabel,
        color: isActive ? accentColor : "rgba(255,255,255,0.4)",
        fontWeight: isActive ? "800" : "500",
      }}>
        {tab.label}
      </span>
    </button>
  );
}

// ─── Battles Tab Item ─────────────────────────────────────────────────────────
// Isolated subscriber that owns the live data behind the Battles dot, mirroring iOS's
// BattlesTabBarItem. While mounted (i.e. on every shelled screen) it listens for active
// Pick Up games and incoming challenges, and exposes the priority-colored dot. Listeners
// only attach when signed in. On Knockout Saturdays the active tab also tints lavender.

function BattlesTabItem({ tab, isActive, onClick }) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [hasPendingTurn, setHasPendingTurn] = useState(false);
  const [hasIncoming, setHasIncoming] = useState(false);

  useEffect(() => {
    if (!uid) {
      setHasPendingTurn(false);
      setHasIncoming(false);
      return undefined;
    }
    const unsubGames = listenToActivePickUpGames(uid, (games) => {
      // isUserTurn already requires the game to be active, so this is the pending-turn set.
      setHasPendingTurn(games.some((g) => isUserTurn(g, uid)));
    });
    const unsubCh = listenToIncomingChallenges(uid, (list) => setHasIncoming(list.length > 0));
    return () => {
      unsubGames();
      unsubCh();
    };
  }, [uid]);

  const accentColor = isKnockoutSaturday() ? KNOCKOUT_LAVENDER : APP_THEME;

  let dotColor = null;
  if (!isActive) {
    if (shouldShowKnockoutDot()) dotColor = KNOCKOUT_LAVENDER;
    else if (hasPendingTurn) dotColor = APP_THEME;
    else if (hasIncoming) dotColor = TEAL;
  }

  const handleClick = () => {
    markKnockoutDotSeen(); // tapping Battles dismisses the Saturday reminder for the day
    onClick();
  };

  return (
    <TabItem
      tab={tab}
      isActive={isActive}
      onClick={handleClick}
      dotColor={dotColor}
      accentColor={accentColor}
    />
  );
}

// ─── App Store Banner ─────────────────────────────────────────────────────────
// Permanent strip above the bottom nav — taps open the App Store listing.

const APP_STORE_URL = "https://apps.apple.com/app/rosterable/id6761739479";

function AppStoreBanner() {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.appStoreBanner}
    >
      <img src={appstore} alt="Download Rosterable on the App Store" style={styles.appStoreImg} />
    </a>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
// Wraps page content between TopBar and BottomNav.
// Screens should NOT add their own top padding for the wordmark —
// the TopBar handles that. Screens keep their own internal top padding.

export default function Layout({ children }) {
  const location = useLocation();

  const hideShell = ["/draft", "/draft-complete", "/streak", "/showcase"].some(p =>
    location.pathname.startsWith(p)
  );

  if (hideShell) {
    return <>{children}</>;
  }

  return (
    <div style={styles.page}>
      <TopBar />
      <div style={styles.content}>
        {children}
      </div>
      <AppStoreBanner />
      <BottomNav />
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flex: 1,
  },

  // Top bar
  topBar: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    maxWidth: "480px",
    width: "100%",
    margin: "0 auto",
  },
  topBarRule: {
    height: "1px",
    background: "rgba(255,255,255,0.07)",
  },
  // Fixed-width left/right slots so the centered wordmark stays centered
  topBarSide: {
    width: "40px",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  // Free Play toggle (Home only). Out of the flex flow so the wordmark stays
  // centered. Switch stacked above the "FREE PLAY" label, right:16 — clears the
  // centered wordmark even on narrow phones.
  freePlayToggle: {
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    background: "none",
    border: "none",
    padding: "2px",
    cursor: "pointer",
    zIndex: 2,
  },
  freePlayLabel: {
    fontSize: "8.5px",
    fontWeight: "800",
    letterSpacing: "0.8px",
    whiteSpace: "nowrap",
    lineHeight: 1,
    transition: "color 0.15s ease",
  },
  freePlayTrack: {
    position: "relative",
    width: "30px",
    height: "16px",
    borderRadius: "999px",
    flexShrink: 0,
    transition: "background 0.15s ease",
  },
  freePlayKnob: {
    position: "absolute",
    top: "2px",
    left: "2px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
    transition: "transform 0.15s ease",
  },
  wordmarkBtn: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  wordmark: {
    height: "22px",
    width: "auto",
  },

  // Menu button + dropdown
  menuBtn: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    cursor: "pointer",
    padding: 0,
  },
  menuIcon: {
    fontSize: "17px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.85)",
    lineHeight: 1,
  },
  menuBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 90,
  },
  menuPanel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: "16px",
    minWidth: "190px",
    background: "rgb(20,20,32)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: "6px",
    boxShadow: "0 16px 44px rgba(0,0,0,0.55)",
    zIndex: 100,
  },
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.85)",
    fontSize: "14px",
    fontWeight: "600",
    padding: "11px 14px",
    borderRadius: "9px",
    cursor: "pointer",
    letterSpacing: "0.2px",
  },
  menuDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    margin: "2px 10px",
  },

  // App Store banner
  appStoreBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 20px",
    textDecoration: "none",
    cursor: "pointer",
    touchAction: "manipulation",
  },
  appStoreImg: {
    height: "44px",
    width: "auto",
    display: "block",
  },

  // Bottom nav
  bottomRule: {
    height: "1px",
    background: "rgba(255,255,255,0.07)",
  },
  bottomNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    padding: "6px 12px 0",
    paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
    background: "rgba(10,10,20,0.96)",
    backdropFilter: "blur(12px)",
    position: "sticky",
    bottom: 0,
    zIndex: 50,
  },
  tabItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px 0",
  },
  tabIconWrapper: {
    position: "relative",
    width: "46px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tabPill: {
    position: "absolute",
    inset: 0,
    background: APP_THEME,
    borderRadius: "10px",
    boxShadow: `0 2px 8px ${APP_THEME}66`,
  },
  tabDot: {
    position: "absolute",
    top: "1px",
    right: "5px",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    zIndex: 2,
  },
  tabIcon: {
    lineHeight: 1,
    display: "block",
  },
  tabLabel: {
    fontSize: "8px",
    letterSpacing: "0.8px",
    lineHeight: 1,
  },
};
